import { useState } from 'react';
import { conversationWorkspaceStrings } from '../../i18n/conversationWorkspace';
import { useTheme } from '../../theme/ThemeProvider';
import { createShare } from '../../lib/conversationsApi';
import { sharePath } from '../../shareRoute';

const EXPIRY_OPTIONS = [
  { label: 'Never', hours: null },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
] as const;

interface ShareDialogProps {
  resourceType: 'conversation' | 'response';
  resourceId: string;
  messageIndex?: number;
  onClose: () => void;
}

export function ShareDialog({ resourceType, resourceId, messageIndex, onClose }: ShareDialogProps) {
  const { language } = useTheme();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;
  const [includeFiles, setIncludeFiles] = useState(false);
  const [includeSources, setIncludeSources] = useState(true);
  const [expiryHours, setExpiryHours] = useState<number | null>(null);
  const [status, setStatus] = useState<'idle' | 'creating' | 'ready' | 'error'>('idle');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setStatus('creating');
    try {
      const grant = await createShare({
        resourceType,
        resourceId,
        ...(resourceType === 'response' ? { messageIndex } : {}),
        includeFiles,
        includeSources,
        ...(expiryHours ? { expiresAt: Date.now() + expiryHours * 60 * 60 * 1000 } : {}),
      });
      setLink(`${window.location.origin}${sharePath(grant.token)}`);
      setStatus('ready');
    } catch (_) {
      setStatus('error');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable -- the link is still selectable text */ }
  };

  return (
    <div className="ozira-cw-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="ozira-cw-panel ozira-share-dialog" role="dialog" aria-modal="true" aria-label={t('share')} onClick={(e) => e.stopPropagation()}>
        <h3>{t('share')}</h3>
        {status === 'ready' ? (
          <>
            <p className="ozira-share-dialog__hint">{t('shareLinkHint')}</p>
            <div className="ozira-share-dialog__link-row">
              <input type="text" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" />
              <button type="button" onClick={() => void copyLink()}>{copied ? 'Copied' : t('copy')}</button>
            </div>
          </>
        ) : (
          <>
            <label className="ozira-share-dialog__row">
              <span>Include files</span>
              <input type="checkbox" checked={includeFiles} onChange={(e) => setIncludeFiles(e.target.checked)} />
            </label>
            <label className="ozira-share-dialog__row">
              <span>Include sources</span>
              <input type="checkbox" checked={includeSources} onChange={(e) => setIncludeSources(e.target.checked)} />
            </label>
            <label className="ozira-share-dialog__row">
              <span>Link expires</span>
              <select value={expiryHours ?? ''} onChange={(e) => setExpiryHours(e.target.value ? Number(e.target.value) : null)}>
                {EXPIRY_OPTIONS.map((option) => <option key={option.label} value={option.hours ?? ''}>{option.label}</option>)}
              </select>
            </label>
            {status === 'error' ? <p className="ozira-share-dialog__error" role="alert">{t('shareFailed')}</p> : null}
            <button type="button" className="ozira-share-dialog__create" disabled={status === 'creating'} onClick={() => void create()}>
              {status === 'creating' ? 'Creating…' : t('share')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
